import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// One box for the whole shop: slabs, offcuts, jobs, accounts, paperwork.
export const GlobalSearch = ({
  testid = "global-search",
  autoFocus = false,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [filter, setFilter] = useState("all");
  const [tabs, setTabs] = useState([]);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(null);
      setTabs([]);
      return;
    }
    const params = { q: q.trim() };
    if (filter !== "all") {
      params.type = filter;
      params.limit = 20;
    }
    const timer = setTimeout(() => {
      api
        .get("/search", { params })
        .then(({ data }) => {
          setRes(data);
          setCursor(0);
          setOpen(true);
          // tabs track the categories of the full (unfiltered) query
          if (filter === "all")
            setTabs((data.groups || []).map((g) => ({ key: g.key, label: g.label })));
        })
        .catch(() => setRes(null));
    }, 250);
    return () => clearTimeout(timer);
  }, [q, filter]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    const onClickAway = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickAway);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickAway);
    };
  }, []);

  const flat = (res?.groups || []).flatMap((g) =>
    g.hits.map((h) => ({ ...h, group: g.label })),
  );

  const go = (hit) => {
    if (!hit) return;
    setOpen(false);
    setQ("");
    setRes(null);
    onNavigate?.();
    navigate(hit.url);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!flat.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[cursor] || flat[0]);
    }
  };

  let index = -1;

  return (
    <div ref={boxRef} className="relative w-full" data-testid={testid}>
      <MagnifyingGlass
        size={16}
        weight="bold"
        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500"
      />
      <input
        ref={inputRef}
        data-testid={`${testid}-input`}
        value={q}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQ(e.target.value);
          setFilter("all");
        }}
        onFocus={() => res && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search"
        aria-label="Search everything"
        className="h-10 w-full rounded-full border border-zinc-800 bg-zinc-900/70 pl-10 pr-9 text-sm text-zinc-200 placeholder:text-zinc-500 focus:border-indigo-400/50 focus:bg-zinc-900 focus:outline-none"
      />
      {q && (
        <button
          type="button"
          data-testid={`${testid}-clear`}
          aria-label="Clear search"
          onClick={() => {
            setQ("");
            setRes(null);
            setFilter("all");
            setTabs([]);
            inputRef.current?.focus();
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
        >
          <X size={14} weight="bold" />
        </button>
      )}

      {open && res && (
        <div
          data-testid={`${testid}-panel`}
          className="absolute left-0 right-0 top-12 z-50 max-h-[70vh] overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 p-1.5 shadow-[0_10px_30px_-3px_rgba(0,0,0,0.6)]"
        >
          {tabs.length > 1 && (
            <div
              data-testid={`${testid}-tabs`}
              className="sticky top-0 z-10 mb-1 flex flex-wrap gap-1 border-b border-zinc-800/80 bg-zinc-950/95 px-1 pb-2 pt-1 backdrop-blur"
            >
              {[{ key: "all", label: "All" }, ...tabs].map((t) => {
                const on = filter === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`${testid}-tab-${t.key}`}
                    data-active={on ? "1" : undefined}
                    onClick={() => {
                      setFilter(t.key);
                      setCursor(0);
                    }}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-150 ${
                      on
                        ? "bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-400/40"
                        : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}
          {flat.length === 0 && (
            <p
              className="px-3 py-4 text-sm text-zinc-500"
              data-testid={`${testid}-empty`}
            >
              Nothing found for “{res.query}”
            </p>
          )}
          {(res.groups || []).map((g) => (
            <div key={g.key} className="mb-1 last:mb-0">
              <div className="px-2.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                {g.label}
              </div>
              {g.hits.map((h) => {
                index += 1;
                const active = index === cursor;
                return (
                  <button
                    key={`${g.key}-${h.id}`}
                    type="button"
                    data-testid={`${testid}-result-${index}`}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => go(h)}
                    className={`flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors duration-150 ${
                      active ? "bg-indigo-500/15" : "hover:bg-zinc-900"
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-zinc-100">
                        {h.title}
                      </span>
                      {h.subtitle && (
                        <span className="block truncate text-xs text-zinc-500">
                          {h.subtitle}
                        </span>
                      )}
                    </span>
                    {h.code && (
                      <span className="font-mono-data shrink-0 rounded border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 text-[10px] text-zinc-400">
                        {h.code}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Phone header: an icon that opens the same search full width.
export const GlobalSearchMobile = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="global-search-open"
        aria-label="Search"
        onClick={() => setOpen(true)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-800 text-zinc-500 transition-colors duration-150 hover:border-indigo-400/40 hover:text-indigo-300"
      >
        <MagnifyingGlass size={17} weight="bold" />
      </button>
      {open && (
        <div
          data-testid="global-search-sheet"
          className="fixed inset-0 z-50 bg-black/80 p-4 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
        >
          <div
            className="mx-auto max-w-md rounded-lg border border-zinc-800 bg-zinc-950 p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <GlobalSearch
              testid="global-search-mobile"
              autoFocus
              onNavigate={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};
